import { ORPCError } from '@orpc/client';
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import { currentTenantId, orpc } from './lib/api.ts';
import { loadConfig, signUpOpen } from './lib/config.ts';
import { queryClient } from './lib/query.ts';
import type { Me } from './lib/session.ts';

// Routes. Everything under /t/:tenant is one workspace; the guard below makes sure the person is
// signed in and belongs to it before any page of it loads.

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/sign-in', name: 'sign-in', component: () => import('./pages/auth/SignInPage.vue'), meta: { public: true } },
    { path: '/sign-up', name: 'sign-up', component: () => import('./pages/auth/SignUpPage.vue'), meta: { public: true } },
    { path: '/forgot-password', name: 'forgot-password', component: () => import('./pages/auth/ForgotPasswordPage.vue'), meta: { public: true } },
    { path: '/reset-password', name: 'reset-password', component: () => import('./pages/auth/ResetPasswordPage.vue'), meta: { public: true } },
    { path: '/verified', name: 'verified', component: () => import('./pages/auth/VerifiedPage.vue'), meta: { public: true } },
    { path: '/accept-invitation/:id', name: 'accept-invitation', component: () => import('./pages/auth/AcceptInvitationPage.vue'), meta: { public: true } },
    { path: '/welcome', name: 'welcome', component: () => import('./pages/WelcomePage.vue') },
    { path: '/', name: 'home', component: () => import('./pages/HomeRedirect.vue') },
    {
      path: '/t/:tenant',
      component: () => import('./layouts/AppLayout.vue'),
      children: [
        { path: '', name: 'overview', component: () => import('./pages/OverviewPage.vue') },
        { path: 'submissions', name: 'submissions', component: () => import('./pages/SubmissionsPage.vue') },
        { path: 'projects', name: 'projects', component: () => import('./pages/ProjectsPage.vue') },
        { path: 'projects/:projectId', name: 'project', component: () => import('./pages/ProjectPage.vue') },
        { path: 'forms/:formId', name: 'form', component: () => import('./pages/FormPage.vue') },
        { path: 'channels', name: 'channels', component: () => import('./pages/ChannelsPage.vue') },
        { path: 'members', name: 'members', component: () => import('./pages/MembersPage.vue') },
        { path: 'settings', name: 'settings', component: () => import('./pages/SettingsPage.vue') },
      ],
    },
    { path: '/:rest(.*)*', redirect: '/' },
  ],
});

/** The signed-in user, or `undefined` when there is no session. */
export async function loadMe(): Promise<Me | undefined> {
  try {
    return await queryClient.query(orpc.me.get.queryOptions({ staleTime: 60_000 }));
  } catch (error) {
    if (error instanceof ORPCError && error.code === 'UNAUTHORIZED') return undefined;
    throw error;
  }
}

function signInRoute(to: RouteLocationNormalized) {
  return { name: 'sign-in', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } };
}

router.beforeEach(async (to) => {
  await loadConfig();
  // With sign-up closed the page has nothing to offer; an old link to it lands on sign-in.
  if (to.name === 'sign-up' && !signUpOpen.value) return { name: 'sign-in', query: to.query };
  if (to.meta['public'] === true) return true;
  const me = await loadMe();
  if (!me) return signInRoute(to);

  const slug = to.params['tenant'];
  if (typeof slug !== 'string') return true;
  const membership = me.memberships.find((candidate) => candidate.tenantSlug === slug);
  if (!membership) return { name: 'home' };
  if (currentTenantId.value !== membership.tenantId) {
    // Queries carry no tenant in their keys, so another workspace's answers must not stay in the cache.
    queryClient.removeQueries({ predicate: (query) => JSON.stringify(query.queryKey) !== JSON.stringify(orpc.me.get.queryOptions().queryKey.value) });
    currentTenantId.value = membership.tenantId;
  }
  return true;
});
